



































import { computed, defineComponent, onMounted, ref, ssrRef, useFetch } from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import { usePageStore } from '~/stores/page';
import { extendProductsForBox } from '~/utils/solepro/products';
import Spinner from '~/components/solepro/Spinner.vue';

import { ProductBox } from '~/utils/types/solepro/Product';
import CategoryInfos from '~/components/solepro/Category/CategoryInfos.vue';
import CategoryProductList from '~/components/solepro/Category/CategoryProductList.vue';

import {
  useCategory,
  useFacet,
  useUiHelpers,
  useUiState,
} from '~/composables';

import facetGetters from '~/modules/catalog/category/getters/facetGetters';

import { usePrice } from '~/modules/catalog/pricing/usePrice';
import { useCategoryContent } from '~/modules/catalog/category/components/cms/useCategoryContent';
import { useTraverseCategory } from '~/modules/catalog/category/helpers/useTraverseCategory';
import { useCategoryStore } from '~/modules/catalog/category/stores/category';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import { categoryView, categoryProductCardTrack, categoryNavigationTrack } from '~/helpers/trackings/24orepro/categories';
import { getProductFilterByCategoryCommand } from '~/modules/catalog/category/components/filters/command/getProductFilterByCategoryCommand';
import { useApi } from '~/composables/useApi';
import { createProductAttributeFilterInput } from '~/modules/catalog/category/composables/useFacet/input/createProductAttributeFilterInput';
import { isFilterEnabled, isFilterEnabledInCategory } from '~/modules/catalog/category/config/FiltersConfig';
import HTMLContent from '~/components/solepro/HTMLContent.vue';

import type { SortingModel } from '~/modules/catalog/category/composables/useFacet/sortingOptions';
import type { Pagination } from '~/composables/types';
import type { Aggregation } from '~/modules/GraphQL/types';


export default defineComponent({
  name: 'CategoryPage',
  components: {
    CategoryInfos,
    HTMLContent,
    CategoryProductGrid: () => import('~/modules/catalog/category/components/views/CategoryProductGrid.vue'),
    CategoryProductList,
    Spinner,
  },
  transition: 'fade',
  setup() {
    const { routeData } = usePageStore();
    const { getContentData } = useCategoryContent();
    const { loadCategoryMeta } = useCategory();
    const { addTags } = useCache();
    const { query } = useApi();
    const uiHelpers = useUiHelpers();
    const products = ssrRef<ProductBox[]>([]);
    const allCategories = ref([]);
    const filters = ref<Aggregation[]>([]);
    const relatedFilterCode = ref(null);
    const firstEditorialBox = ref('');
    const secondEditorialBox = ref('');
    const firstEditorialBoxPosition = ref(4);
    const secondEditorialBoxPosition = ref(10);
    const totalResults = ref(0);
    const sortBy = ref<SortingModel>({ selected: '', options: [] });
    const pagination = ref<Pagination>({});

    const productContainerElement = ref<HTMLElement | null>(null);

    const {
      toggleFilterSidebar,
    } = useUiState();

    const { result, search } = useFacet();

    const categoryMeta = ref(null);

    const { activeCategory, loadCategoryTree } = useTraverseCategory();
    const activeCategoryName = computed(() => activeCategory.value?.name ?? '');
    const activeCategoryDescription = computed(() => activeCategory.value?.description ?? '');

    const trackPageView = async (categoryName) => {
      categoryView(categoryName);
    };

    const categoryUid = routeData.uid;

    const { fetch } = useFetch(async () => {
      if (!activeCategory.value) {
        await loadCategoryTree();
      }

      const categoryStore = useCategoryStore();
      const filterQueryParams = createProductAttributeFilterInput({ ...uiHelpers.getFacetsFromURL(), category_uid: categoryUid })

      allCategories.value = categoryStore.rawCategories?.items[0].children;

      const [content, categoryMetaData, fetchedFilters] = await Promise.all([
        getContentData(categoryUid as string),
        loadCategoryMeta({ category_uid: routeData.value?.uid }),
        getProductFilterByCategoryCommand.execute(filterQueryParams, query),
        search({ ...uiHelpers.getFacetsFromURL(), category_uid: categoryUid }),
      ]);

      relatedFilterCode.value = content.relatedFilterCode;
      categoryMeta.value = categoryMetaData;
      firstEditorialBox.value = content.firstEditorialBox;
      secondEditorialBox.value = content.secondEditorialBox;
      firstEditorialBoxPosition.value = content.firstEditorialBoxPosition;
      secondEditorialBoxPosition.value = content.secondEditorialBoxPosition;
      
      filters.value = getCurrentFilters(fetchedFilters);

      products.value = extendProductsForBox(allCategories.value, facetGetters.getProducts(result.value)) ?? [];
      sortBy.value = facetGetters.getSortOptions(result.value);
      totalResults.value = result.value.data.total;
      pagination.value = facetGetters.getPagination(result.value);

      const tags = [{ prefix: CacheTagPrefix.View, value: routeData.uid }];
      const productTags = products.value.map((product) => ({
        prefix: CacheTagPrefix.Product,
        value: product.uid,
      }));

      addTags([...tags, ...productTags]);
      await trackPageView(activeCategoryName.value);
    });

    const isPriceLoaded = ref(false);

    onMounted(async () => {
      const { getPricesBySku } = usePrice();
      if (products.value.length > 0) {
        const skus = products.value.map((item) => item.sku);
        const priceData = await getPricesBySku(skus, pagination.value.itemsPerPage);
        products.value = products.value.map((product) => {
          const priceRange = priceData.items.find((item) => item.sku === product.sku)?.price_range;

          if (priceRange) {
            return {
              ...product,
              price_range: priceRange,
            };
          }

          return { ...product };
        });
      }

      isPriceLoaded.value = true;
    });

    const goToPage = (page: number) => {
      
      if (page != 0) {
        categoryNavigationTrack(activeCategoryName.value, page)
      }

      uiHelpers.changePage(page, false);
      fetch();
      productContainerElement.value.scrollIntoView();
    };

    const doChangeItemsPerPage = (itemsPerPage: number) => {
      uiHelpers.changeItemsPerPage(itemsPerPage, false);
      goToPage(0);
    };

    const getCurrentFilters = (fetchedFilters) => {
      return fetchedFilters
        .filter((filter) => isFilterEnabled(filter.attribute_code))
        .filter((filter) => isFilterEnabledInCategory(filter.enabled_categories, routeData.uid))
        .filter((filter) => filter.attribute_code != relatedFilterCode.value)
        .sort((a, b) => {
          const getPosition = (item) => item.position !== null ? item.position : Infinity;
            return getPosition(a) - getPosition(b);
      });
    }

    const onReloadProducts = () => {
      goToPage(0);
    };

    const trackCategoryProductCard = (productName) => {
      const categoryName = activeCategoryName.value ? activeCategoryName.value : "search";
      categoryProductCardTrack(categoryName, productName)
    }

    return {
      isPriceLoaded,
      ...uiHelpers,
      toggleFilterSidebar,
      pagination,
      products,
      sortBy,
      filters,
      relatedFilterCode,
      firstEditorialBox,
      secondEditorialBox,
      firstEditorialBoxPosition,
      secondEditorialBoxPosition,
      activeCategoryName,
      activeCategoryDescription,
      totalResults,
      routeData,
      trackCategoryProductCard,
      doChangeItemsPerPage,
      productContainerElement,
      categoryMeta,
      onReloadProducts,
      goToPage
    };
  },
  head() {
    return {
      titleTemplate: `24Ore Professionale - ${this.activeCategoryName}`,
      ...getMetaInfo(this.categoryMeta)
    }
  }
});
